<template>
  <div class="buyer-account-details-wrapper">
    <!-- Floating Navigation Menu -->
    <nav class="section-nav">
      <ul class="section-nav__list">
        <li
          v-for="item in navigationItems"
          :key="item.id"
          class="section-nav__item"
        >
          <a
            :href="`#${item.id}`"
            class="section-nav__link"
            @click.prevent="scrollToSection(item.id, item.collapsibleKey)"
          >
            <CIcon
              :name="item.icon"
              size="sm"
              class="mr-1"
            />
            {{ item.label }}
          </a>
        </li>
      </ul>
    </nav>

    <!-- Main Content -->
    <div class="buyer-account-details-content">
      <CCard
        v-if="getSafe(() => buyerAccountDetails.testOnly)"
        class="test-account-banner bg-danger p-3 text-white"
      >
        <strong>
          <CIcon
            name="cilWarning"
            class="mr-1"
          />
          This is a test-only account
        </strong>
      </CCard>

      <CCard
        v-if="getSafe(() => isAdminApprovalRequirementNotMet)"
        class="test-account-banner bg-warning px-3 py-3 text-white mb-3"
        :class="{
          'test-banner--position-2': getSafe(() => buyerAccountDetails.testOnly)
        }"
      >
        <strong class="d-flex align-items-center">
          <CIcon
            name="cilWarning"
            class="mr-1"
          />
          Admin Approval is pending
          <CButton
            color="success"
            class="ml-auto"
            size="sm"
            @click="warningApproveModal = true"
          >
            Approve
          </CButton>
        </strong>
      
        <h5
          v-if="buyerAccountDetails && buyerAccountDetails.nextPendingItemForFinalApproval"
          class="mb-0"
        >
          <i><small>Next Pending Item for Final Approval:</small> {{ buyerAccountDetails && buyerAccountDetails.nextPendingItemForFinalApproval }}</i>
        </h5>
      </CCard>

      <CCard id="section-account-details">
        <CCardHeader>
          <div class="d-flex justify-content-between">
            <div class="d-flex align-items-center">
              <CIcon
                name="cil-user"
                class="mr-2"
              />
              <strong>Account Details</strong>
            </div>

            <div class="d-flex align-items-center">
              <CDropdown
                v-if="buyerAccountDetails && buyerAccountDetails.enabled === true"
                class="d-inline-block mr-2"
                color="warning"
                size="sm"
              >
                <template #toggler-content>
                  <span>Disable</span>
                </template>
                <CDropdownItem @click="showDisableAccountModal('Paused')">
                  Pause
                </CDropdownItem>
                <CDropdownItem @click="showDisableAccountModal('Deactivated')">
                  Deactivate
                </CDropdownItem>
                <CDropdownItem @click="showDisableAccountModal('Banned')">
                  Ban
                </CDropdownItem>
              </CDropdown>

              <CButton
                v-if="buyerAccountDetails && buyerAccountDetails.enabled === false"
                color="success"
                size="sm"
                class="mr-2"
                :disabled="buyerAccountDetails.disableReason === 'Deactivated' || buyerAccountDetails.disableReason === 'Banned'"
                @click="showEnableAccountModal"
              >
                Enable
              </CButton>

              <CButton
                size="sm"
                :color="editMode ? 'danger' : 'primary'"
                @click="toggleEditMode"
              >
                {{ editMode ? 'Cancel' : 'Edit Details' }}
              </CButton>
            </div>
          </div>
        </CCardHeader>

        <CCardBody>
          <div v-if="buyerAccountDetails && buyerAccountDetails.id">
            <div>
              <div v-if="!editMode">
                <AccountDetailsCommonInfoReadOnly
                  type="buyer"
                  :account-details="buyerAccountDetails"
                />
              </div>
              <div v-else>
                <AccountDetailsCommonInfoEdit
                  type="buyer"
                  :account-details="buyerAccountDetails"
                  @updated="toggleEditMode"
                />
              </div>
            </div>
            <div v-if="!editMode">
              <h3>
                Other Details
              </h3>
              <dl class="row">
                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Time Zone
                </dt>
                <dd
                  class="col-sm-9"
                >
                  {{ getSafe(() => timezonesMap[buyerAccountDetails.timeZoneId].label) }}
                </dd>

                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  No. of Employees
                </dt>
                <dd
                  class="col-sm-9"
                >
                  {{ buyerAccountDetails.buyerRegistrationDetails.numberOfEmployeesRange }}
                </dd>
                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Call Purchase Volume
                </dt>
                <dd
                  class="col-sm-9"
                >
                  {{ buyerAccountDetails.buyerRegistrationDetails.callPurchaseVolumeRange }}
                </dd>
              </dl>
            </div>
          </div>

          <SignedDocumentBundles
            account-type="buyer"
            :account-id="getSafe(() => buyerAccountDetails.id)"
            :signed-bundles="getSafe(() => signedBundles)"
          />

          <AccountRequirements
            class="mt-4"
            :account-requirements="buyerAccountRequirements"
          />
        </CCardBody>
      </CCard>

      <div id="section-billing">
        <AccountBillingDetails
          v-if="buyerAccountDetails"
          :account-billing-config="buyerAccountDetails.billingConfiguration"
          :account-id="accountId"
          @prepay-status-changed="reloadBuyerAccountDetails"
        />
      </div>

      <div id="section-traffic">
        <AccountTrafficDetails
          v-if="buyerAccountDetails"
          :account="buyerAccountDetails"
          type="buyer"
        />
      </div>

      <div id="section-auto-pause">
        <BuyerAutoPauseParameters
          v-if="buyerAccountDetails"
          :account-id="buyerAccountDetails.id"
          :buyer-auto-pause-parameters="buyerAccountAutoPauseParameters"
        />
      </div>

      <div id="section-budget">
        <BuyerAccountBudgetDetails
          v-if="buyerAccountDetails"
          :account-id="buyerAccountDetails.id"
          :buyer-account-budget="buyerAccountDetails.budget"
        />
      </div>

      <div id="section-rtb">
        <BuyerRtbSettings
          v-if="buyerAccountDetails"
          :account-id="buyerAccountDetails.id"
          :rtb-settings="buyerAccountDetails.rtbSettings"
        />
      </div>

      <CCard
        v-if="buyerAccountDetails"
        id="section-users"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('users')"
        >
          <CIcon
            name="cil-people"
            class="mr-2"
          />
          <strong>Users</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.users.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.users.expanded">
          <CCardBody>
            <AccountUsers
              ref="accountUsers"
              :account-id="buyerAccountDetails.id"
              account-type="Buyer"
              :account-name="buyerAccountDetails.name"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="buyerAccountDetails"
        id="section-campaigns"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('campaigns')"
        >
          <CIcon
            name="cil-basket"
            class="mr-2"
          />
          <strong>Buyer Campaigns</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.campaigns.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.campaigns.expanded">
          <CCardBody>
            <BuyerCampaignsList
              :buyer-campaigns="buyerCampaigns"
              :is-loading="isLoading"
              :prefiltered-by-account="true"
              :hide-card="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <div id="section-blocked-channels">
        <BuyerBlockedChannels
          v-if="buyerAccountDetails"
          :account-id="buyerAccountDetails.id"
        />
      </div>

      <CCard
        v-if="buyerAccountDetails"
        id="section-creatives"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('creatives')"
        >
          <CIcon
            name="cil-image"
            class="mr-2"
          />
          <strong>Creative Reviews</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.creatives.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.creatives.expanded">
          <CCardBody>
            <BuyerCreatives
              ref="buyerCreatives"
              :account-id="getSafe(() => buyerAccountDetails.id)"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="buyerAccountDetails && buyerAccountDetails.billingConfiguration.prePay"
        id="section-credit-card-charges"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('creditCardCharges')"
        >
          <CIcon
            name="cil-credit-card"
            class="mr-2"
          />
          <strong>Credit Card Charges</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.creditCardCharges.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.creditCardCharges.expanded">
          <CCardBody>
            <BuyerCreditCardCharges
              ref="buyerCreditCardCharges"
              :account-id="buyerAccountDetails.id"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="buyerAccountDetails"
        id="section-notes"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('notes')"
        >
          <CIcon
            name="cil-notes"
            class="mr-2"
          />
          <strong>Notes</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.notes.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.notes.expanded">
          <CCardBody>
            <ReviewNotes
              ref="reviewNotes"
              :parent-object="buyerAccountDetails"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="buyerAccountDetails && buyerAccountDetails.registrationId"
        id="section-registration-checklist"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('checklist')"
        >
          <CIcon
            name="cil-check-circle"
            class="mr-2"
          />
          <strong>Review Checklist</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.checklist.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.checklist.expanded">
          <CCardBody>
            <RegistrationRequestChecklist
              ref="registrationChecklist"
              :registration-request-id="buyerAccountDetails.registrationId"
              :account-id="accountId"
              :next-pending-item-for-final-approval="buyerAccountDetails.nextPendingItemForFinalApproval"
              type="buyer"
              :lazy-load="true"
              @pending-status-changed="reloadBuyerAccountDetails"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <div>
        <CButton 
          v-if="isAdminApprovalRequirementNotMet"
          type="submit"
          size="lg"
          color="success"
          class="mb-3"
          @click="warningApproveModal = true"
        >
          Final Approve 
        </CButton>
      </div>

      <CModal
        v-if="warningApproveModal"
        title="Confirm final approve"
        color="success"
        :show.sync="warningApproveModal"
      >
        Are you sure you want to approve this buyer?

        <p class="mt-3">
          <CRow
            class="form-group form-row"
          >
            <CCol
              sm="3"
              class="text-right pr-4"
            >
              Account Type
            </CCol>
            <CCol>
              <div class="d-flex align-items-center mb-3">
                <CSwitch
                  variant="3d"
                  color="primary"
                  horizontal
                  size="sm"
                  class="mr-2"
                  :checked.sync="buyerAccountDetails.billingConfiguration.prePay"
                />
                <div class="m-0">
                  {{ buyerAccountDetails.billingConfiguration.prePay ? 'Pre-Pay' : 'Post-Pay' }}
                </div>
                <CIcon
                  v-c-tooltip="'Pre Pay: pay upfront with credit card. Post Pay: use credit line, pay via invoice according to the payment terms. Switch from Post Pay to Pre Pay is not possible.'"
                  name="cil-lightbulb"
                  size="sm"
                />
              </div>
            </CCol>
          </CRow>
        </p>

        <p class="alert alert-warning mt-2">
          <strong>Note:</strong>
          The account will be active after the final approval has been granted, so that a buyer can create campaigns and purchase calls.
        </p>

        <template #footer>
          <ButtonLoading
            color="primary"
            :loading="isLoading"
            @click="approveAccount"
          >
            Approve
          </ButtonLoading>

          <CButton
            color="danger"
            @click="warningApproveModal = false"
          >
            Cancel
          </CButton>
        </template>
      </CModal>

      <CModal
        title="Confirm disable"
        color="warning"
        :show.sync="disableAccountModal"
      >
        Are you sure you want to {{ selectedDisableReason === 'Paused' ? 'pause' : selectedDisableReason === 'Deactivated' ? 'deactivate' : 'ban' }} this account?

        <CTextarea
          v-model="remarks"
          class="mt-2"
          name="Remarks"
          label="Remarks"
          :is-valid="areRemarksValid"
          invalid-feedback="Remarks are mandatory for disabling"
          small
          required
          rows="3"
          :debounce="500"
          description="Remarks will be sent in the email notification to the client"
        />

        <CTextarea
          v-if="buyerAccountDetails !== null"
          v-model="accountNameNote"
          class="mt-2"
          name="AccountNameNote"
          :label="'Enter account name for confirmation - ' + buyerAccountDetails.name"
          :is-valid="isAccountNameInputAsExpected(buyerAccountDetails)"
          invalid-feedback="Enter account name (company name) to confirm account disabling"
          small
          required
          rows="1"
          :debounce="500"
        />

        <template #footer>
          <ButtonLoading
            color="primary"
            :loading="isDisableEnableLoading"
            :disabled="!isAccountNameInputAsExpected(buyerAccountDetails) || !areRemarksValid"
            @click="confirmDisableAccount"
          >
            Disable
          </ButtonLoading>

          <CButton
            color="danger"
            @click="disableAccountModal = false; accountNameNote = ''; remarks = '';"
          >
            Cancel
          </CButton>
        </template>
      </CModal>

      <CModal
        title="Confirm enable"
        color="success"
        :show.sync="enableAccountModal"
      >
        Are you sure you want to enable this account?

        <CTextarea
          v-model="remarks"
          class="mt-2"
          name="Remarks"
          label="Remarks"
          :is-valid="areRemarksValid"
          invalid-feedback="Remarks are mandatory for enabling"
          small
          required
          rows="3"
          :debounce="500"
          description="Remarks will be sent in the email notification to the client"
        />

        <CTextarea
          v-if="buyerAccountDetails !== null"
          v-model="accountNameNote"
          class="mt-2"
          name="AccountNameNote"
          :label="'Enter account name for confirmation - ' + buyerAccountDetails.name"
          :is-valid="isAccountNameInputAsExpected(buyerAccountDetails)"
          invalid-feedback="Enter account name (company name) to confirm account enabling"
          small
          required
          rows="1"
          :debounce="500"
        />

        <template #footer>
          <ButtonLoading
            color="primary"
            :loading="isDisableEnableLoading"
            :disabled="!isAccountNameInputAsExpected(buyerAccountDetails) || !areRemarksValid"
            @click="confirmEnableAccount"
          >
            Enable
          </ButtonLoading>

          <CButton
            color="danger"
            @click="enableAccountModal = false; accountNameNote = ''; remarks = '';"
          >
            Cancel
          </CButton>
        </template>
      </CModal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import isEmpty from 'lodash.isempty'

import AccountBillingDetails from './AccountBillingDetails.vue'
import AccountTrafficDetails from './AccountTrafficDetails.vue'
import BuyerAccountBudgetDetails from './BuyerAccountBudgetDetails.vue'
import SignedDocumentBundles from './SignedDocumentBundles.vue'
import AccountDetailsCommonInfoReadOnly from './AccountDetailsCommonInfoReadOnly.vue'
import AccountDetailsCommonInfoEdit from './AccountDetailsCommonInfoEdit.vue'
import AccountRequirements from './AccountRequirements.vue'
import AccountUsers from '@/views/Users/AccountUsers.vue'
import ReviewNotes from '../ReviewNotes/ReviewNotes.vue'
import RegistrationRequestChecklist from '@/views/RegistrationRequests/RegistrationRequestChecklist.vue'
import BuyerBlockedChannels from './BuyerBlockedChannels.vue'
import { showSuccessMessage } from '@/notification-utils'
import { COMMON_ACCOUNT_REQUIREMENTS } from '@/store/modules/accounts.store'
import BuyerRtbSettings from './BuyerRtbSettings.vue'
import BuyerCampaignsList from '../Campaigns/buyer/BuyerCampaignsList.vue'
import BuyerAutoPauseParameters from './Buyer/BuyerAutoPauseParameters.vue'
import BuyerCreatives from '@/views/Creatives/BuyerCreatives.vue'
import BuyerCreditCardCharges from './BuyerCreditCardCharges.vue'
import hashNavigationMixin from '@/mixins/hash-navigation-mixin'

export default {
  name: 'BuyerAccountDetails',

  components: {
    ReviewNotes,
    AccountBillingDetails,
    AccountTrafficDetails,
    SignedDocumentBundles,
    AccountDetailsCommonInfoReadOnly,
    AccountDetailsCommonInfoEdit,
    AccountRequirements,
    BuyerAccountBudgetDetails,
    AccountUsers,
    RegistrationRequestChecklist,
    BuyerRtbSettings,
    BuyerCampaignsList,
    BuyerBlockedChannels,
    BuyerAutoPauseParameters,
    BuyerCreatives,
    BuyerCreditCardCharges
  },

  mixins: [hashNavigationMixin],

  data() {
    return  {
      editMode: false,
      warningApproveModal: false,
      isLoading: false,
      COMMON_ACCOUNT_REQUIREMENTS,
      disableAccountModal: false,
      enableAccountModal: false,
      isDisableEnableLoading: false,
      accountNameNote: '',
      remarks: '',
      selectedDisableReason: '',
      accountAction: '',
      // Collapsible sections state
      collapsibleSections: {
        users: { expanded: false, loaded: false },
        campaigns: { expanded: false, loaded: false },
        creatives: { expanded: false, loaded: false },
        creditCardCharges: { expanded: false, loaded: false },
        notes: { expanded: false, loaded: false },
        checklist: { expanded: false, loaded: false }
      }
    }
  },

  computed: {
    ...mapState('accounts', [
      'buyerAccountDetails',
      'buyerAccountRequirements',
      'buyerAccountAutoPauseParameters',
    ]),

    ...mapState('category', [
      'categories'
    ]),

    ...mapState('trafficSource', [
      'trafficSources'
    ]),

    ...mapState('campaign', ['buyerCampaigns']),

    ...mapGetters('timezones', [
      'timezonesMap'
    ]),

    categoriesMap() {
      return this.categories.reduce(function(map, obj) {
        map[obj.id] = obj
        return map
      }, {})
    },

    trafficSourcesMap() {
      return this.trafficSources.reduce(function(map, obj) {
        map[obj.id] = obj
        return map
      }, {})
    },

    signedBundles() {
      return this.buyerAccountDetails?.eSignature?.signedBundles ?? this.buyerAccountDetails?.eSignature?.signedBundleIds?.map(b => ({ id: b }))
    },

    isAdminApprovalRequirementNotMet() {
      return !isEmpty(this.buyerAccountRequirements?.requirements) && !this.buyerAccountRequirements?.requirements[COMMON_ACCOUNT_REQUIREMENTS.ADMIN_APPROVAL_REQUIREMENT]
    },

    navigationItems() {
      const items = [
        { id: 'section-account-details', label: 'Account Details', icon: 'cil-user' },
        { id: 'section-billing', label: 'Account Billing', icon: 'cil-dollar' },
        { id: 'section-traffic', label: 'Traffic Details', icon: 'cil-transfer' },
        { id: 'section-auto-pause', label: 'Auto Pause', icon: 'cil-media-pause' },
        { id: 'section-budget', label: 'Budget', icon: 'cil-wallet' },
        { id: 'section-rtb', label: 'RTB Settings', icon: 'cil-settings' },
        { id: 'section-users', label: 'Users', icon: 'cil-people', collapsibleKey: 'users' },
        { id: 'section-campaigns', label: 'Buyer Campaigns', icon: 'cil-basket', collapsibleKey: 'campaigns' },
        { id: 'section-blocked-channels', label: 'Blocked Channels', icon: 'cil-ban' },
        { id: 'section-creatives', label: 'Creative Reviews', icon: 'cil-image', collapsibleKey: 'creatives' }
      ]

      if (this.buyerAccountDetails?.billingConfiguration?.prePay) {
        items.push({ id: 'section-credit-card-charges', label: 'CC Charges', icon: 'cil-credit-card', collapsibleKey: 'creditCardCharges' })
      }

      items.push({ id: 'section-notes', label: 'Notes', icon: 'cil-notes', collapsibleKey: 'notes' })

      if (this.buyerAccountDetails?.registrationId) {
        items.push({ id: 'section-registration-checklist', label: 'Review Checklist', icon: 'cil-check-circle', collapsibleKey: 'checklist' })
      }

      return items
    }
  },

  async created() {
    this.accountId = this.$route.params.id

    if (this.accountId) {
      try {
        await this.getBuyerAccountDetails(this.accountId)
        await this.getBuyerAccountRequirements(this.accountId)
        await this.getBuyerAutoPauseParameters(this.accountId)
        document.title = this.buyerAccountDetails?.name
      } catch (e) {
        //
      }
    }

    await this.getCategories()
    await this.getTrafficSources()

    this.SET_CURRENT_REGISTRATION_TYPE('buyer')
  },

  methods: {
    ...mapActions('accounts', [
      'getBuyerAccountDetails',
      'getBuyerAccountRequirements',
      'markAccountAdminApprovalRequirement',
      'getBuyerAutoPauseParameters',
      'disableAccount',
      'enableAccount'
    ]),

    ...mapActions('category', {
      getCategories: 'list'
    }),

    ...mapActions('trafficSource', {
      getTrafficSources: 'list'
    }),

    ...mapActions('campaign', ['fetchBuyerCampaigns']),

    ...mapMutations('registrationRequests', [
      'SET_CURRENT_REGISTRATION_TYPE'
    ]),

    async reloadBuyerAccountDetails() {
      await this.getBuyerAccountDetails(this.accountId)
    },

    toggleEditMode() {
      this.editMode = !this.editMode
    },

    async approveAccount() {
      try {
        this.isLoading = true

        await this.markAccountAdminApprovalRequirement({
          accountId: this.buyerAccountDetails.id,
          approve: true,
          accountType: 'buyer'
        })

        showSuccessMessage('Admin Approval done successfully')

        this.warningApproveModal = false

        await this.getBuyerAccountRequirements(this.accountId)
      } catch (error) {
        //
      } finally {
        this.isLoading = false
      }
    },

    async getCampaigns() {
      try {
        this.isLoading = true
        await this.fetchBuyerCampaigns({ detailed: true, accountId: this.accountId })
      } catch (error) {
        //
      } finally {
        this.isLoading = false
      }
    },

    showDisableAccountModal(disableReason) {
      this.accountAction = 'disable'
      this.selectedDisableReason = disableReason
      this.disableAccountModal = true
    },

    showEnableAccountModal() {
      this.accountAction = 'enable'
      this.enableAccountModal = true
    },

    async confirmDisableAccount() {
      try {
        this.isDisableEnableLoading = true
        await this.disableAccount({
          accountId: this.buyerAccountDetails.id,
          type: 'buyer',
          remarks: this.remarks,
          disableReason: this.selectedDisableReason
        })

        showSuccessMessage('Successfully disabled account ' + this.buyerAccountDetails.id)

        await this.getBuyerAccountDetails(this.accountId)
      } catch (error) {
        //
      } finally {
        this.disableAccountModal = false
        this.isDisableEnableLoading = false
        this.accountNameNote = ''
        this.remarks = ''
        this.selectedDisableReason = ''
      }
    },

    async confirmEnableAccount() {
      try {
        this.isDisableEnableLoading = true
        await this.enableAccount({
          accountId: this.buyerAccountDetails.id,
          type: 'buyer',
          remarks: this.remarks
        })

        showSuccessMessage('Successfully enabled account ' + this.buyerAccountDetails.id)

        await this.getBuyerAccountDetails(this.accountId)
      } catch (error) {
        //
      } finally {
        this.enableAccountModal = false
        this.isDisableEnableLoading = false
        this.accountNameNote = ''
        this.remarks = ''
      }
    },

    isAccountNameInputAsExpected(account) {
      if (this.accountAction === 'enable' && this.accountNameNote === account.name) {
        return true
      }

      if (this.accountAction === 'disable' && this.accountNameNote === account.name) {
        return true
      }

      return false
    },

    areRemarksValid() {
      return !!this.remarks.length
    },

    getDisableReasonBadgeColor(disableReason) {
      switch (disableReason) {
        case 'Paused':
          return 'warning'
        case 'Deactivated':
          return 'dark'
        case 'Banned':
          return 'danger'
        default:
          return 'secondary'
      }
    },

    async toggleSection(sectionName) {
      const section = this.collapsibleSections[sectionName]
      section.expanded = !section.expanded

      if (section.expanded && !section.loaded) {
        await this.loadSectionData(sectionName)
        section.loaded = true
      }
    },

    async loadSectionData(sectionName) {
      switch (sectionName) {
        case 'users':
          this.$refs.accountUsers?.loadUsers()
          break
        case 'campaigns':
          await this.getCampaigns()
          break
        case 'creatives':
          this.$refs.buyerCreatives?.loadCreatives()
          break
        case 'creditCardCharges':
          this.$refs.buyerCreditCardCharges?.loadChargeReceipts()
          break
        case 'notes':
          this.$refs.reviewNotes?.fetchNotes()
          break
        case 'checklist':
          this.$refs.registrationChecklist?.loadData()
          break
      }
    },

    // Helper method for hash navigation mixin
    getCollapsibleKeyForSection(sectionId) {
      const navItem = this.navigationItems.find(item => item.id === sectionId)
      return navItem?.collapsibleKey
    },

    // Helper method for hash navigation mixin
    async expandSection(collapsibleKey) {
      if (this.collapsibleSections[collapsibleKey] && !this.collapsibleSections[collapsibleKey].expanded) {
        await this.toggleSection(collapsibleKey)
      }
    }
  }

}
</script>

<style scoped lang="scss">
.buyer-account-details-wrapper {
  position: relative;
  margin-left: -0.75rem;
  margin-right: -0.75rem;
}

.buyer-account-details-content {
  margin-right: 145px;

  @media (max-width: 1200px) {
    margin-right: 0;
  }
}

.section-nav {
  position: fixed;
  top: 120px;
  right: 18px;
  width: 125px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;

  @media (max-width: 1200px) {
    display: none;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
  }

  &__item {
    margin: 0;
  }

  &__link {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    color: #4f5d73;
    font-size: 11px;
    text-decoration: none;
    transition: background-color 0.15s, color 0.15s;

    &:hover {
      background-color: #ebedef;
      color: #321fdb;
      text-decoration: none;
    }
  }
}

.clickable {
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}
</style>